import { Container, Divider } from "@material-ui/core";
import { Add } from "@material-ui/icons";
import { Grid } from "@mui/material";
import axios from "axios";
import {
  ButtonGroupTop,
  CustomTable,
  ModalError,
  ModalSuccess,
  Page,
  PrimaryButton,
  SearchTextInput,
  SelectInput,
} from "components";
import { HeaderTitle } from "layouts";
import { getTotalPage } from "lib";
import { debounce } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router";
import { hardBaseUrl } from "services/urlConstant";
import { globalStyles } from "styles";
import { getErrors } from "utils";
import ModalFormParameterUser from "./Components/ModalFormParameterUser";

function User({ userLogin }) {
  const classes = globalStyles();
  const history = useHistory();
  const location = useLocation();
  const token = localStorage.getItem("token");
  const typeWeb = localStorage.getItem("typeWeb");
  const isMpis = typeWeb === "mpis";
  const usedUserApi = isMpis ? "/user" : "/publisher/user";
  const headers = {
    Authorization: `Bearer ${token}`,
  };

  const urlParams = new URLSearchParams(location.search);
  const paramsSearch = urlParams?.get("search");
  const paramsPage = urlParams?.get("page");
  const paramsSize = urlParams?.get("size");
  const paramsRole = urlParams?.get("role_id");
  const paramsSort = urlParams?.get("sort");
  const [loadingPage, setLoadingPage] = useState(true);
  const [optionRole, setOptionRole] = useState([]);
  const [dataTable, setDataTable] = useState([]);
  const [queryParams, setQueryParams] = useState({
    page: Number(paramsPage) || 1,
    size: Number(paramsSize) || 10,
    search: paramsSearch || "",
    role_id: paramsRole || "",
    sort: paramsSort || "",
  });
  const [tableTotalPage, setTableTotalPage] = useState(1);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalPreload, setModalPreload] = useState(null);

  const handleChangePageParams = (value, key) => {
    urlParams.set(key, value);
    history.replace({
      pathname: location.pathname,
      search: urlParams.toString(),
    });
  };
  const handleChangeQueryParams = (value, key) => {
    setQueryParams(currentState => ({
      ...currentState,
      ...(key !== "page" && { page: 1 }),
      [key]: value,
    }));
    handleChangePageParams(value, key);
    if (key !== "page") {
      handleChangePageParams(1, "page");
    }
  };

  const getDataTable = async () => {
    try {
      setLoadingPage(true);
      const res = await axios.get(`${hardBaseUrl}${usedUserApi}`, {
        headers,
        params: {
          ...queryParams,
          ...(isMpis && { type: "admin" }),
        },
      });

      const { data, meta } = res?.data;
      setDataTable(data);
      const pageCount = getTotalPage(meta?.total, queryParams?.size || 1);
      setTableTotalPage(pageCount);
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingPage(false);
    }
  };
  const debounceDataTable = useCallback(
    debounce(() => {
      getDataTable();
    }, 500),
    [queryParams]
  );
  const getOptionRole = async () => {
    try {
      setLoadingPage(true);
      const res = await axios.get(`${hardBaseUrl}/role`, {
        headers,
        params: {
          type: isMpis ? "admin" : "publisher",
        },
      });
      setOptionRole(res?.data?.data);
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingPage(false);
    }
  };
  const handleDelete = async ({ user_id }) => {
    try {
      setLoadingPage(true);
      const res = await axios.delete(
        `${hardBaseUrl}${usedUserApi}/${user_id}`,
        { headers }
      );

      if (res.data.message === "success") {
        ModalSuccess("Data Deleted Successfully").then(result => {
          if (result.isConfirmed === true) {
            getDataTable();
          }
        });
      }
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingPage(false);
    }
  };

  useEffect(() => {
    getOptionRole();
  }, []);
  useEffect(() => {
    const isSearching = Boolean(queryParams?.search);
    if (isSearching) {
      debounceDataTable();
      return () => {
        debounceDataTable.cancel();
      };
    } else {
      getDataTable();
    }
  }, [queryParams, debounceDataTable]);

  return (
    <Page className={classes.root} title="User">
      <Container maxWidth={false}>
        <HeaderTitle title="Parameter" breadcrumbData={breadcrumbData} />
        <ButtonGroupTop />
        <Divider className={classes.flatDivider} />
        <Grid container justifyContent="space-between" my="16px">
          <Grid item>
            <SearchTextInput
              placeholder="Search"
              value={queryParams?.search}
              onChange={e =>
                handleChangeQueryParams(e?.target?.value, "search")
              }
              disabled={loadingPage}
            />
          </Grid>
          <Grid item>
            <Grid container columnSpacing={1} alignItems="center">
              <Grid item>
                <SelectInput
                  label="Role"
                  options={optionRole}
                  optionKey="role_id"
                  optionLabel="name"
                  value={queryParams?.role_id || ""}
                  onChange={e =>
                    handleChangeQueryParams(e?.target?.value, "role_id")
                  }
                  placeholder="All Role"
                  width={200}
                  disabled={loadingPage}
                />
              </Grid>
              <Grid item>
                <PrimaryButton
                  label="Add User"
                  startIcon={<Add />}
                  onClick={() => {
                    setModalVisible(true);
                    setModalPreload(null);
                  }}
                  size="large"
                  disabled={loadingPage}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <CustomTable
          isLoading={loadingPage}
          columnTable={columnTable}
          data={dataTable}
          page={queryParams?.page}
          pageSize={queryParams?.size}
          pageCount={tableTotalPage}
          handleChangePage={(_, page) => handleChangeQueryParams(page, "page")}
          handleChangePageSize={e =>
            handleChangeQueryParams(e?.target?.value, "size")
          }
          sortValue={queryParams?.sort}
          onRequestSort={sortBy =>
            handleChangeQueryParams(
              `${sortBy} ${
                queryParams.sort === `${sortBy} asc` ? "desc" : "asc"
              }`,
              "sort"
            )
          }
          isHaveAction
          handleEdit={item => {
            setModalVisible(true);
            setModalPreload(item?.user_id);
          }}
          deleteConfirmation
          deleteConfirmationTitle="Delete User"
          deleteConfirmationKey="name"
          handleDelete={item => handleDelete(item)}
        />
        {modalVisible && (
          <ModalFormParameterUser
            open={modalVisible}
            onClose={() => setModalVisible(false)}
            userLogin={userLogin}
            preload={modalPreload}
            getDataTable={getDataTable}
          />
        )}
      </Container>
    </Page>
  );
}

const breadcrumbData = [
  {
    label: "Home",
    link: "/admin/dashboard",
  },
  {
    label: "Parameter",
    link: "/admin/parameter/user",
  },
  {
    label: "User",
    active: true,
  },
];
const columnTable = [
  {
    name: "name",
    title: "User Name",
    sort_key: "name",
    useSort: true,
  },
  {
    name: "role",
    title: "Role",
    sort_key: "role",
    useSort: true,
    renderText: item => item?.name,
  },
  {
    name: "phone",
    title: "Phone Number",
    sort_key: "phone",
    useSort: true,
  },
  {
    name: "email",
    title: "Email",
    sort_key: "email",
    useSort: true,
  },
];
export default User;
